import {
  useAuthStore,
  useUserRole,
  useIsAdmin,
  useIsCustomer,
  useIsNonProfitUser,
  useBusinessId,
  useTokenExpiresAt,
  useSessionWarning,
  usePasswordResetState,
  usePasswordResetEmail,
  usePasswordResetError,
  useAccountLockout,
  type PasswordResetState,
  type AccountLockout
} from '../../application/store/authStore';
import { CurrentUser } from '../../domain/entities/User';
import { UserRole } from '../../domain/value-objects/UserRole';
import { LoginCommand } from '../../application/dto/auth/LoginCommand';
import { RegisterUserCommand } from '../../application/dto/auth/RegisterUserCommand';
import { RegisterResult } from '../../application/dto/auth/LoginResult';
import { ForgotPasswordCommand } from '../../application/dto/auth/ForgotPasswordCommand';
import { ResetPasswordCommand } from '../../application/dto/auth/ResetPasswordCommand';

/**
 * useAuth - Custom hook for consuming authentication state and actions
 *
 * Provides comprehensive authentication functionality including:
 * - Login/register/logout
 * - Token refresh and session management
 * - Password reset workflows
 * - Account lockout handling
 */
export interface UseAuthResult {
  // Core authentication state
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  role: UserRole | null;
  businessId: string | null;
  isAdmin: boolean;
  isCustomer: boolean;
  isNonProfitUser: boolean;

  // Token and session state
  tokenExpiresAt: string | null;
  sessionWarningShown: boolean;

  // Password reset state
  passwordResetState: PasswordResetState;
  passwordResetEmail: string | null;
  passwordResetError: string | null;

  // Account lockout state
  accountLockout: AccountLockout;

  // Core authentication actions
  login: (command: LoginCommand) => Promise<void>;
  register: (command: RegisterUserCommand) => Promise<RegisterResult | undefined>;
  logout: () => Promise<void>;
  loadCurrentUser: () => Promise<void>;
  clearError: () => void;

  // Token and session actions
  startTokenRefreshTimer: () => void;
  stopTokenRefreshTimer: () => void;
  refreshAccessToken: () => Promise<void>;
  showSessionWarning: () => void;
  hideSessionWarning: () => void;
  extendSession: () => Promise<void>;

  // Password reset actions
  forgotPassword: (command: ForgotPasswordCommand) => Promise<void>;
  resetPassword: (command: ResetPasswordCommand) => Promise<void>;
  clearPasswordResetState: () => void;

  // Account lockout actions
  setAccountLockout: (lockout: AccountLockout) => void;
  clearAccountLockout: () => void;
}

/**
 * Hook to access authentication state and actions
 */
export function useAuth(): UseAuthResult {
  // Core state
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const error = useAuthStore((state) => state.error);

  // Token and session state
  const tokenExpiresAt = useTokenExpiresAt();
  const sessionWarningShown = useSessionWarning();

  // Password reset state
  const passwordResetState = usePasswordResetState();
  const passwordResetEmail = usePasswordResetEmail();
  const passwordResetError = usePasswordResetError();

  // Account lockout state
  const accountLockout = useAccountLockout();

  // Core actions
  const login = useAuthStore((state) => state.login);
  const register = useAuthStore((state) => state.register);
  const logout = useAuthStore((state) => state.logout);
  const loadCurrentUser = useAuthStore((state) => state.loadCurrentUser);
  const clearError = useAuthStore((state) => state.clearError);

  // Token and session actions
  const startTokenRefreshTimer = useAuthStore((state) => state.startTokenRefreshTimer);
  const stopTokenRefreshTimer = useAuthStore((state) => state.stopTokenRefreshTimer);
  const refreshAccessToken = useAuthStore((state) => state.refreshAccessToken);
  const showSessionWarning = useAuthStore((state) => state.showSessionWarning);
  const hideSessionWarning = useAuthStore((state) => state.hideSessionWarning);
  const extendSession = useAuthStore((state) => state.extendSession);

  // Password reset actions
  const forgotPassword = useAuthStore((state) => state.forgotPassword);
  const resetPassword = useAuthStore((state) => state.resetPassword);
  const clearPasswordResetState = useAuthStore((state) => state.clearPasswordResetState);

  // Account lockout actions
  const setAccountLockout = useAuthStore((state) => state.setAccountLockout);
  const clearAccountLockout = useAuthStore((state) => state.clearAccountLockout);

  // Computed values
  const role = useUserRole();
  const businessId = useBusinessId();
  const isAdmin = useIsAdmin();
  const isCustomer = useIsCustomer();
  const isNonProfitUser = useIsNonProfitUser();

  return {
    // Core authentication state
    user,
    isAuthenticated,
    isLoading,
    error,
    role,
    businessId,
    isAdmin,
    isCustomer,
    isNonProfitUser,

    // Token and session state
    tokenExpiresAt,
    sessionWarningShown,

    // Password reset state
    passwordResetState,
    passwordResetEmail,
    passwordResetError,

    // Account lockout state
    accountLockout,

    // Core authentication actions
    login,
    register,
    logout,
    loadCurrentUser,
    clearError,

    // Token and session actions
    startTokenRefreshTimer,
    stopTokenRefreshTimer,
    refreshAccessToken,
    showSessionWarning,
    hideSessionWarning,
    extendSession,

    // Password reset actions
    forgotPassword,
    resetPassword,
    clearPasswordResetState,

    // Account lockout actions
    setAccountLockout,
    clearAccountLockout
  };
}
